import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  InputAdornment,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  AttachMoney as CurrencyIcon,
} from '@mui/icons-material';
import useCurrencyStore from '../../stores/currencyStore';
import useNotificationStore from '../../stores/notificationStore';
import { MagicLoader, TextField as DesignTextField, Select as DesignSelect } from '../../design-system';
import { useTranslation } from 'react-i18next';

const CurrencyConfiguration = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currencies = useCurrencyStore(state => state.currencies);
  const fetchCurrencies = useCurrencyStore(state => state.fetchCurrencies);
  const createCurrency = useCurrencyStore(state => state.createCurrency);
  const updateCurrency = useCurrencyStore(state => state.updateCurrency);
  const deleteCurrency = useCurrencyStore(state => state.deleteCurrency);
  const restoreCurrency = useCurrencyStore(state => state.restoreCurrency);
  const setBaseCurrency = useCurrencyStore(state => state.setBaseCurrency);
  const updateExchangeRates = useCurrencyStore(state => state.updateExchangeRates);
  const isLoading = useCurrencyStore(state => state.isLoading);
  const error = useCurrencyStore(state => state.error);
  const { showSuccess, showError } = useNotificationStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState({ 
    open: false, 
    item: {
      code: '',
      name: '',
      symbol: '',
      symbolPosition: 'before',
      decimalPlaces: 2,
      thousandsSeparator: ',',
      decimalSeparator: '.',
      isRTL: false,
      exchangeRate: 1,
      isActive: true,
      countries: []
    }
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [exchangeRatesDialog, setExchangeRatesDialog] = useState({ open: false, rates: {} });
  
  // Use ref to track if we've already fetched
  const hasFetched = useRef(false);
  
  // Fetch currencies on mount only once
  useEffect(() => {
    if (!hasFetched.current && (!currencies || currencies.length === 0) && !isLoading) {
      hasFetched.current = true;
      fetchCurrencies();
    }
  }, [currencies, isLoading, fetchCurrencies]);
  
  // Filter currencies based on search
  const filterCurrencies = () => {
    if (!currencies || !Array.isArray(currencies)) return [];
    if (!searchTerm) return currencies;
    
    return currencies.filter(currency => 
      currency.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  // Handle add/edit dialog
  const handleOpenDialog = (currency = null) => {
    setEditDialog({
      open: true,
      item: currency || {
        code: '',
        name: '',
        symbol: '',
        symbolPosition: 'before',
        decimalPlaces: 2,
        thousandsSeparator: ',',
        decimalSeparator: '.',
        isRTL: false,
        exchangeRate: 1,
        isActive: true,
        countries: []
      }
    });
  };
  
  const handleCloseDialog = () => {
    setEditDialog({ 
      open: false, 
      item: {
        code: '',
        name: '',
        symbol: '',
        symbolPosition: 'before',
        decimalPlaces: 2,
        thousandsSeparator: ',',
        decimalSeparator: '.',
        isRTL: false,
        exchangeRate: 1,
        isActive: true,
        countries: []
      }
    });
  };
  
  // Handle delete dialog
  const handleOpenDeleteDialog = (currency) => {
    setDeleteDialog({ open: true, item: currency });
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, item: null });
  };
  
  // Handle exchange rates dialog
  const handleOpenExchangeRates = () => {
    const rates = {};
    if (currencies && Array.isArray(currencies)) {
      currencies.forEach(currency => {
        rates[currency.code] = currency.exchangeRate || 1;
      });
    }
    setExchangeRatesDialog({ open: true, rates });
  };
  
  const handleCloseExchangeRates = () => {
    setExchangeRatesDialog({ open: false, rates: {} });
  };
  
  // Handle save currency
  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const { item } = editDialog;
      
      if (item._id) {
        // Update existing
        await updateCurrency(item._id, item);
        showSuccess(t('settings.currency.updateSuccess', 'Currency updated successfully'));
      } else {
        // Create new
        await createCurrency(item);
        showSuccess(t('settings.currency.createSuccess', 'Currency created successfully'));
      }
      
      // Force refresh the currencies after successful save
      await fetchCurrencies(true);
      handleCloseDialog();
    } catch (error) {
      showError(error.message || t('settings.currency.saveFailed', 'Failed to save currency'));
    }
  };
  
  // Handle delete currency
  const handleDelete = async () => {
    try {
      await deleteCurrency(deleteDialog.item._id);
      showSuccess(t('settings.currency.deleteSuccess', 'Currency deleted successfully'));
      // Force refresh the currencies after successful delete
      await fetchCurrencies(true);
      handleCloseDeleteDialog();
    } catch (error) {
      showError(error.message || t('settings.currency.deleteFailed', 'Failed to delete currency'));
    }
  };

  // Handle restore currency
  const handleRestore = async (currencyId) => {
    try {
      await restoreCurrency(currencyId);
      showSuccess(t('settings.currency.restoreSuccess', 'Currency restored successfully'));
      // Force refresh the currencies after successful restore
      await fetchCurrencies(true);
    } catch (error) {
      showError(error.message || t('settings.currency.restoreFailed', 'Failed to restore currency'));
    }
  };
  
  // Handle set base currency
  const handleSetBaseCurrency = async (currencyId) => {
    try {
      await setBaseCurrency(currencyId);
      showSuccess(t('settings.currency.baseCurrencySet', 'Base currency updated successfully'));
    } catch (error) {
      showError(error.message || t('settings.currency.baseCurrencyFailed', 'Failed to set base currency'));
    }
  };
  
  // Handle update exchange rates
  const handleUpdateExchangeRates = async () => {
    try {
      await updateExchangeRates(exchangeRatesDialog.rates);
      showSuccess(t('settings.currency.exchangeRatesUpdated', 'Exchange rates updated successfully'));
      handleCloseExchangeRates();
    } catch (error) {
      showError(error.message || t('settings.currency.exchangeRatesFailed', 'Failed to update exchange rates'));
    }
  };
  
  // Format currency example
  const formatExample = (currency) => {
    try {
      const amount = 1234.56;
      if (currency.formatAmount) {
        return currency.formatAmount(amount);
      }
      
      const formatted = amount.toFixed(currency.decimalPlaces || 2);
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, currency.thousandsSeparator || ',');
      
      const number = parts.join(currency.decimalSeparator || '.');
      
      if (currency.symbolPosition === 'after') {
        return `${number}${currency.isRTL ? ' ' : ''}${currency.symbol}`;
      }
      return `${currency.symbol}${currency.isRTL ? ' ' : ''}${number}`;
    } catch {
      return `${currency.symbol} 1,234.56`;
    }
  };
  
  if (isLoading && (!currencies || currencies.length === 0)) {
    return <MagicLoader />;
  }
  
  const filteredCurrencies = filterCurrencies();
  
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('settings.currency.title', 'Currency Configuration')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settings.currency.description', 'Manage currencies and exchange rates for your system')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<TrendingUpIcon />}
            onClick={handleOpenExchangeRates}
            disabled={!currencies || currencies.length === 0}
          >
            {t('settings.currency.updateRates', 'Update Rates')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('settings.currency.addCurrency', 'Add Currency')}
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder={t('settings.currency.searchPlaceholder', 'Search currencies...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {filteredCurrencies.length === 0 ? (
        <Alert severity="info">
          {searchTerm 
            ? t('settings.currency.noSearchResults', 'No currencies found matching your search.')
            : t('settings.currency.noCurrencies', 'No currencies configured. Click "Add Currency" to create one.')
          }
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.currency.code', 'Code')}</TableCell>
                <TableCell>{t('settings.currency.name', 'Name')}</TableCell>
                <TableCell>{t('settings.currency.symbol', 'Symbol')}</TableCell>
                <TableCell>{t('settings.currency.example', 'Example')}</TableCell>
                <TableCell align="right">{t('settings.currency.exchangeRate', 'Exchange Rate')}</TableCell>
                <TableCell align="center">{t('settings.currency.status', 'Status')}</TableCell>
                <TableCell align="center">{t('settings.currency.base', 'Base')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCurrencies.map((currency) => (
                <TableRow key={currency._id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {currency.code}
                    </Typography>
                  </TableCell>
                  <TableCell>{currency.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={currency.symbol} 
                      size="small" 
                      variant="outlined"
                      icon={<CurrencyIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {formatExample(currency)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {currency.exchangeRate?.toFixed(4) || '1.0000'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={currency.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                      color={currency.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={currency.isBaseCurrency ? t('settings.currency.baseCurrency', 'Base Currency') : t('settings.currency.setAsBase', 'Set as Base Currency')}>
                      <IconButton
                        size="small"
                        onClick={() => !currency.isBaseCurrency && handleSetBaseCurrency(currency._id)}
                        disabled={currency.isBaseCurrency}
                      >
                        {currency.isBaseCurrency ? <StarIcon color="primary" /> : <StarBorderIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(currency)}
                    >
                      <EditIcon />
                    </IconButton>
                    {currency.isActive ? (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(currency)}
                        disabled={currency.isBaseCurrency}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : (
                      <Tooltip title={t('settings.currency.restore', 'Restore Currency')}>
                        <IconButton
                          size="small"
                          onClick={() => handleRestore(currency._id)}
                          color="success"
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Add/Edit Currency Dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editDialog.item?._id ? t('settings.currency.editCurrency', 'Edit Currency') : t('settings.currency.addCurrency', 'Add Currency')}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSave}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.code', 'Currency Code')}
                  value={editDialog.item?.code || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, code: e.target?.value?.toUpperCase() || '' }
                  })}
                  disabled={!!editDialog.item?._id}
                  placeholder="USD"
                  helperText={t('settings.currency.codeHelp', '3-letter ISO code')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.symbol', 'Symbol')}
                  value={editDialog.item?.symbol || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, symbol: e.target?.value || '' }
                  })}
                  placeholder="$"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.name', 'Currency Name')}
                  value={editDialog.item?.name || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, name: e.target?.value || '' }
                  })}
                  placeholder="US Dollar"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t('settings.currency.symbolPosition', 'Symbol Position')}
                  value={editDialog.item?.symbolPosition || 'before'}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, symbolPosition: e.target?.value || 'before' }
                  })}
                >
                  <MenuItem value="before">{t('settings.currency.before', 'Before')}</MenuItem>
                  <MenuItem value="after">{t('settings.currency.after', 'After')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.decimalPlaces', 'Decimal Places')}
                  type="number"
                  value={editDialog.item?.decimalPlaces || 2}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, decimalPlaces: parseInt(e.target?.value) || 0 }
                  })}
                  inputProps={{ min: 0, max: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.thousandsSeparator', 'Thousands Separator')}
                  value={editDialog.item?.thousandsSeparator || ','}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, thousandsSeparator: e.target?.value || '' }
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.decimalSeparator', 'Decimal Separator')}
                  value={editDialog.item?.decimalSeparator || '.'}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, decimalSeparator: e.target?.value || '' }
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <DesignTextField
                  fullWidth
                  label={t('settings.currency.exchangeRate', 'Exchange Rate')}
                  type="number"
                  value={editDialog.item?.exchangeRate || 1}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    item: { ...editDialog.item, exchangeRate: parseFloat(e.target?.value) || 1 }
                  })}
                  helperText={t('settings.currency.exchangeRateHelp', 'Rate relative to base currency')}
                  inputProps={{ step: 0.0001 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editDialog.item?.isRTL || false}
                      onChange={(e) => setEditDialog({
                        ...editDialog,
                        item: { ...editDialog.item, isRTL: e.target?.checked || false }
                      })}
                    />
                  }
                  label={t('settings.currency.isRTL', 'RTL Currency')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editDialog.item?.isActive !== false}
                      onChange={(e) => setEditDialog({
                        ...editDialog,
                        item: { ...editDialog.item, isActive: e.target?.checked !== false }
                      })}
                    />
                  }
                  label={t('settings.currency.isActive', 'Active')}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!editDialog.item?.code || !editDialog.item?.name || !editDialog.item?.symbol}
            startIcon={<SaveIcon />}
          >
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('common.confirmDelete', 'Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('settings.currency.deleteConfirmation', `Are you sure you want to delete "${deleteDialog.item?.name || 'this currency'}"?`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            startIcon={<DeleteIcon />}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Exchange Rates Dialog */}
      <Dialog open={exchangeRatesDialog.open} onClose={handleCloseExchangeRates} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.currency.updateExchangeRates', 'Update Exchange Rates')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.currency.exchangeRateInfo', 'Enter exchange rates relative to the base currency')}
          </Alert>
          <Grid container spacing={2}>
            {Object.entries(exchangeRatesDialog.rates || {}).map(([code, rate]) => (
              <Grid item xs={12} key={code}>
                <TextField
                  fullWidth
                  label={code}
                  type="number"
                  value={rate || 1}
                  onChange={(e) => setExchangeRatesDialog({
                    ...exchangeRatesDialog,
                    rates: {
                      ...exchangeRatesDialog.rates,
                      [code]: parseFloat(e.target?.value) || 1
                    }
                  })}
                  inputProps={{ step: 0.0001 }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExchangeRates}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleUpdateExchangeRates}
            startIcon={<TrendingUpIcon />}
          >
            {t('common.update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(CurrencyConfiguration);